import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Table } from 'antd';
import clsx from 'clsx';
import './style.less';
import { Icon } from 'shared/components/Icon';
import { getSpotlightAccountUTMs, getSpotlightContactUTMs } from 'shared/api';
import { SpotlightUTMRow } from 'types/spotlight';
import { ColumnsType } from 'antd/lib/table';
import Skeleton from 'react-loading-skeleton';

export const SpotlightUtmsDetails: React.FC<{
  visible: boolean;
  setVisible: (v: boolean) => void;
  accountId?: number;
  contactId?: number;
}> = ({ visible, setVisible, accountId, contactId }) => {
  const [data, setData] = useState<SpotlightUTMRow[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(
    (p = 1) => {
      if (!accountId && !contactId) return;

      setLoading(true);

      const id = contactId ?? accountId;
      const fn = contactId ? getSpotlightContactUTMs : getSpotlightAccountUTMs;

      /* @ts-ignore */
      fn(id, p).then(({ data }) => {
        setTotal(data.total);
        setData(data.results);
        setLoading(false);
      });
    },
    [accountId, contactId],
  );

  useEffect(() => {
    if (visible) {
      fetchData();
    }
  }, [fetchData, visible]);

  const close = () => {
    setPage(1);
    setVisible(false);
  };

  const pagination = {
    current: page,
    pageSize: 20,
    total,
    showSizeChanger: false,
    onChange: (page: number) => {
      fetchData(page);
    },
    itemRender: (
      _: number,
      type: 'page' | 'prev' | 'next' | 'jump-prev' | 'jump-next',
      originalElement: React.ReactElement<HTMLElement>,
    ) => {
      switch (type) {
        case 'prev':
          return (
            <button className="ant-pagination-item-link" type="button" tabIndex={-1}>
              <Icon icon="chevron" width={16} height={16} />
            </button>
          );
        case 'next':
          return (
            <button className="ant-pagination-item-link" type="button" tabIndex={-1}>
              <Icon icon="chevron" width={16} height={16} />
            </button>
          );
        default:
          return originalElement;
      }
    },
  };

  const columns: ColumnsType<SpotlightUTMRow> = [
    {
      title: 'UTM Medium',
      dataIndex: 'utmMedium',
      render: v => {
        if (loading) {
          return (
            <Skeleton
              style={{ borderRadius: '12px' }}
              width={30}
              height={12}
              baseColor="#F6F7F9"
            />
          );
        }

        return v || 'n/a';
      },
    },
    {
      title: 'Source',
      dataIndex: 'utmSource',
      render: v => {
        if (loading) {
          return (
            <Skeleton
              style={{ borderRadius: '12px' }}
              width={30}
              height={12}
              baseColor="#F6F7F9"
            />
          );
        }
        return v || 'n/a';
      },
    },
    {
      title: 'Campaign',
      dataIndex: 'utmCampaign',
      render: v => {
        if (loading) {
          return (
            <Skeleton
              style={{ borderRadius: '12px' }}
              width={30}
              height={12}
              baseColor="#F6F7F9"
            />
          );
        }
        return v || 'n/a';
      },
    },
    {
      title: 'Content',
      dataIndex: 'utmContent',
      render: v => {
        if (loading) {
          return (
            <Skeleton
              style={{ borderRadius: '12px' }}
              width={30}
              height={12}
              baseColor="#F6F7F9"
            />
          );
        }
        return v || 'n/a';
      },
    },
    {
      title: 'Term',
      dataIndex: 'utmTerm',
      render: v => {
        if (loading) {
          return (
            <Skeleton
              style={{ borderRadius: '12px' }}
              width={30}
              height={12}
              baseColor="#F6F7F9"
            />
          );
        }
        return v || 'n/a';
      },
    },
    {
      title: 'Sessions',
      dataIndex: 'sessions',
      render: v => {
        if (loading) {
          return (
            <Skeleton
              style={{ borderRadius: '12px' }}
              width={30}
              height={12}
              baseColor="#F6F7F9"
            />
          );
        }
        return v || 'n/a';
      },
    },
  ];

  return (
    <Modal
      style={{ top: 32 }}
      className={clsx('SpotlightUtmsDetails')}
      visible={visible}
      onCancel={close}
    >
      <div className="SpotlightUtmsDetails__header">
        Query Strings
        <div className="SpotlightUtmsDetails__close" onClick={close}>
          <Icon icon="close" width={24} height={24} />
        </div>
      </div>
      <div className="SpotlightUtmsDetails__body">
        <Table
          className="SpotlightUtmsDetails__table"
          rowKey="id"
          dataSource={loading ? new Array(20).fill({}) : data}
          columns={columns}
          pagination={pagination}
          scroll={{ x: 900, scrollToFirstRowOnChange: true }}
          rowClassName={'SpotlightUtmsDetails__row'}
        ></Table>
      </div>
    </Modal>
  );
};
